<template>
    <div>
        <div class="page-section">
            <a-card :bordered="false" style="width: 100%" id="title-card">
                <template slot="title">
                    <div class="v2-tab-hd">
                        <h2>Acompanhamento de preços</h2>
                        <div class="v2-tab-sub">Preços digitados por produto e marca no período</div>
                    </div>
                </template>
                <template slot="extra">
                    <div class="title-actions">
                        <a-button type="default" :loading="exporting" @click="exportExcel">
                            <i class="fa-solid fa-file-excel"></i> Exportar Excel
                        </a-button>
                        <a-button type="danger" :hidden="!isDesktop" @click="handleLimparFiltros">
                            <i class="fa-solid fa-filter-circle-xmark"></i> Limpar Filtros
                        </a-button>
                    </div>
                </template>

                <a-form layout="vertical" style="width: 100%" @submit.prevent="filtrar">
                    <a-row :gutter="[12, 12]">
                        <a-col :xs="24" :sm="12" :md="10" :lg="6">
                            <a-range-picker
                                v-model="rangeDate"
                                style="width: 100%"
                                format="DD/MM/YYYY"
                                :locale="ptBR"
                                :ranges="ranges"
                                :allowClear="false"
                                :placeholder="['Início', 'Fim']"
                            />
                        </a-col>
                        <a-col flex="auto" class="actions-col">
                            <a-form-item class="item-button">
                                <div class="actions-right">
                                    <a-button type="default" icon="search" @click="filtrar">Filtrar</a-button>
                                </div>
                            </a-form-item>
                        </a-col>
                    </a-row>
                </a-form>
            </a-card>
        </div>

        <div class="page-section">
            <a-card :bordered="false" style="width: 100%" class="table-card">
                <div class="table-container">
                    <a-table
                        size="small"
                        :columns="columns"
                        :data-source="products"
                        :row-key="getRowKey"
                        :loading="loading"
                        :pagination="pagination"
                        :scroll="{ x: 'max-content' }"
                        :locale="{ emptyText: 'Nenhum produto digitado no período' }"
                        @change="handleTableChange"
                    >
                        <template slot="name" slot-scope="text, record">
                            <span :class="{ 'parent-name': !record.isBrand }">
                                {{ record.isBrand ? record.pro_observacao : record.pro_descricao }}
                            </span>
                        </template>
                        <template slot="acoes" slot-scope="text, record">
                            <ActionMenu v-if="record.isBrand || !record.children" :actions="getActions(record)" />
                        </template>
                        <template slot="min" slot-scope="text, record">
                            <span class="money-cell" :class="{ 'min-color value-badge': !record.isBrand }">{{ text | currency }}</span>
                        </template>
                        <template slot="avg" slot-scope="text, record">
                            <span class="money-cell" :class="{ 'med-color value-badge': !record.isBrand }">{{ text | currency }}</span>
                        </template>
                        <template slot="max" slot-scope="text, record">
                            <span class="money-cell" :class="{ 'max-color value-badge': !record.isBrand }">{{ text | currency }}</span>
                        </template>
                        <template slot="total" slot-scope="text, record">
                            <span :class="{ 'parent-name': !record.isBrand }">{{ record.isBrand ? record.offers : record.total }}</span>
                        </template>
                    </a-table>
                </div>
            </a-card>
        </div>

        <ProductHistoryV2Modal
            v-if="historyProduct"
            :product="historyProduct"
            :brand="historyBrand"
            :fromDate="fromDate"
            :toDate="toDate"
            @close="closeHistory"
        />
    </div>
</template>

<script>
import moment from "moment";
import ptBR from "ant-design-vue/lib/date-picker/locale/pt_BR";
import ClientService from "@/services/v3/ClientService";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import ProductHistoryV2Modal from "../modals/product-history-v2.modal.vue";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";

const defaultRange = () => [moment().subtract(30, "days"), moment()];

export default {
    name: "PriceTrackingV2Tab",
    components: {
        ProductHistoryV2Modal,
        ActionMenu,
    },
    data() {
        return {
            ptBR,
            service: new ClientService(),
            isDesktop: window.innerWidth >= 720,
            loading: false,
            exporting: false,
            rangeDate: defaultRange(),
            appliedRange: defaultRange(),
            products: [],
            historyProduct: null,
            historyBrand: undefined,
            ranges: {
                Hoje: [moment(), moment()],
                "Últimos 7 dias": [moment().subtract(7, "days"), moment()],
                "Últimos 30 dias": [moment().subtract(30, "days"), moment()],
                "Últimos 90 dias": [moment().subtract(90, "days"), moment()],
                "Esta Semana": [moment().startOf("week"), moment().endOf("week")],
                "Este Mês": [moment().startOf("month"), moment().endOf("month")],
                "Mês Passado": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
                "Este Ano": [moment().startOf("year"), moment()],
            },
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0,
                showSizeChanger: false,
                locale: { items_per_page: "/ página" },
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
            },
            columns: [
                { title: "Nome", key: "name", scopedSlots: { customRender: "name" } },
                { title: "Valor mínimo", dataIndex: "min_valor", key: "min_valor", align: "right", scopedSlots: { customRender: "min" } },
                { title: "Valor Médio", dataIndex: "avg_valor", key: "avg_valor", align: "right", scopedSlots: { customRender: "avg" } },
                { title: "Valor Máximo", dataIndex: "max_valor", key: "max_valor", align: "right", scopedSlots: { customRender: "max" } },
                { title: "Quantidade Digitação", key: "total", align: "center", scopedSlots: { customRender: "total" } },
                { title: "Ações", key: "acoes", align: "center", fixed: "right", width: 70, scopedSlots: { customRender: "acoes" } },
            ],
        };
    },
    watch: {
        "pagination.total"(value) {
            this.$emit("count", value);
        },
    },
    computed: {
        fromDate() {
            return this.appliedRange[0].clone().startOf("day").toISOString();
        },
        toDate() {
            return this.appliedRange[1].clone().startOf("day").toISOString();
        },
    },
    methods: {
        getRowKey(record) {
            return record.isBrand ? `brand-${record.parentKey}-${record.propd_id || record.pro_id || record.pro_observacao}` : `product-${record.prod_id || record.pro_id}`;
        },
        requestDates() {
            return {
                from: this.appliedRange[0].format("YYYY-MM-DD"),
                to: this.appliedRange[1].format("YYYY-MM-DD"),
            };
        },
        shapeProduct(product) {
            const parentKey = product.prod_id || product.pro_id;
            const brands = (product.brands || [])
                .slice()
                .sort((a, b) => String(a.pro_observacao || "").localeCompare(String(b.pro_observacao || "")));
            if (!brands.length) return { ...product, total: product.offers };
            return {
                ...product,
                total: brands.reduce((sum, b) => sum + b.offers, 0),
                min_valor: Math.min(...brands.map((b) => b.min_valor)),
                max_valor: Math.max(...brands.map((b) => b.max_valor)),
                avg_valor: brands.reduce((sum, b) => sum + b.avg_valor, 0) / brands.length,
                children: brands.map((b) => ({ ...b, isBrand: true, parentKey, parent: product })),
            };
        },
        loadProducts(page = this.pagination.current) {
            this.loading = true;
            const { from, to } = this.requestDates();
            this.service
                .getproducts(page, from, `${to}T23:59:59`)
                .then(({ data }) => {
                    this.products = (data.data || []).map(this.shapeProduct);
                    this.pagination.current = page;
                    this.pagination.total = data.total;
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        filtrar() {
            this.appliedRange = [this.rangeDate[0].clone(), this.rangeDate[1].clone()];
            this.loadProducts(1);
        },
        handleLimparFiltros() {
            this.rangeDate = defaultRange();
            this.filtrar();
        },
        handleTableChange(pagination) {
            this.loadProducts(pagination.current);
        },
        getActions(record) {
            return [{ label: "Histórico do produto", icon: "description", iconClass: "action-teal", onClick: () => this.openHistory(record) }];
        },
        openHistory(record) {
            if (record.isBrand) {
                this.historyProduct = record.parent;
                this.historyBrand = record;
            } else {
                this.historyProduct = record;
                this.historyBrand = undefined;
            }
        },
        closeHistory() {
            this.historyProduct = null;
            this.historyBrand = undefined;
        },
        exportExcel() {
            this.exporting = true;
            const { from, to } = this.requestDates();
            this.service
                .getproducts(1, from, to, true)
                .then((resp) => {
                    const file = resp.data instanceof Blob ? resp.data : new Blob([resp.data], { type: "application/vnd.ms-excel" });
                    this.downloadFile(file, "acompanhamentoDePreco.xls");
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.exporting = false;
                });
        },
        downloadFile(file, filename) {
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            if (isIOS) {
                const reader = new FileReader();
                reader.onload = () => this.clickDownload(reader.result, filename);
                reader.readAsDataURL(file);
                return;
            }
            const url = URL.createObjectURL(file);
            this.clickDownload(url, filename);
            URL.revokeObjectURL(url);
        },
        clickDownload(href, filename) {
            const link = document.createElement("a");
            link.href = href;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        handleResize() {
            this.isDesktop = window.innerWidth >= 720;
        },
    },
    mounted() {
        window.addEventListener("resize", this.handleResize);
        this.loadProducts(1);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
    },
};
</script>

<style lang="scss" scoped>
@import "@/sass/commons/_list-page-v2";

.parent-name {
    font-weight: 600;
}
.value-badge {
    padding: 2px 8px;
    border-radius: 6px;
}
.min-color {
    background-color: rgb(218, 244, 218);
    color: #30aa4c;
}
.med-color {
    background-color: #17a2b81a;
    color: #17a2b8;
}
.max-color {
    background-color: rgb(247, 219, 219);
    color: #cd2a2a;
}
</style>
